import * as React from 'react';
import { Check, FileText, Globe, Link2, Zap } from 'lucide-react';
import { AiConfidenceChip } from '@/components/ui/ai-confidence-chip';
import {
  TriCheckbox,
  WorkspaceButton,
  SegmentedControl,
  useToast,
} from '@/components/ui/workspace';
import {
  errorMessage,
  rowsOf,
  useCitationAction,
  useConfirmItems,
  useDeclineItems,
  useKbProposedGroups,
  useKbVendor,
  usePatchProposal,
  type KbOfferedCitation,
  type KbProduct,
  type KbProposal,
  type KbProposedGroup,
} from '@/lib/api/vendor-knowledge';
import { cn } from '@/lib/utils/cn';
import { Chip, Count, KindChip, Linkish, MonoNo, Quote } from '../kb-bits';
import { formatDate, hostPath, plural } from '../kb-logic';
import { NarrowQuote } from './narrow-quote';
import {
  ClassifyPickers,
  DeclineChooser,
  StatementInput,
  emptyResult,
  type ReviewResult,
} from './review-bits';

/**
 * "New from a web page or document" (prototype `capCard()` / `propCard()`):
 * one card per capture that AI read, holding its proposals and the further
 * citations it offers. Proposals the server preselected (confidence 0.85 or
 * more) arrive ticked. Confirm sends the ticked ones in one request
 * (E13-F06 §3) and shows what was confirmed, what became a further
 * citation and what was skipped, with the reason.
 */
export function ProposedGroups({
  vendorId,
  count,
  onResult,
}: {
  vendorId: string;
  count: number;
  onResult: (r: ReviewResult) => void;
}) {
  const groups = useKbProposedGroups(vendorId, true);
  const vendor = useKbVendor(vendorId);
  const rows = rowsOf(groups.data);
  return (
    <ReviewGroupShell
      id="kb-review-new"
      title="New from a web page or document"
      count={count}
      hint="AI read each capture in full and proposed these passages. Confidence 0.85 or higher arrives selected."
    >
      {rows.map((g) => (
        <CaptureCard
          key={`${g.run?.id ?? 'hand'}:${g.version.id}`}
          group={g}
          products={vendor.data?.products ?? []}
          onResult={onResult}
        />
      ))}
      {groups.hasNextPage ? (
        <Linkish onClick={() => void groups.fetchNextPage()} disabled={groups.isFetchingNextPage}>
          Show more captures
        </Linkish>
      ) : null}
    </ReviewGroupShell>
  );
}

export function ReviewGroupShell({
  id,
  title,
  count,
  hint,
  children,
}: {
  id?: string;
  title: string;
  count?: number;
  hint: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div id={id} className="mt-5 first:mt-1" role="group" aria-label={title}>
      <div className="flex items-baseline gap-2 text-[11px] font-semibold uppercase leading-[18px] tracking-[0.06em] text-ink-700">
        <span>{title}</span>
        {count !== undefined ? <Count className="text-[11px]">{count}</Count> : null}
      </div>
      <div className="mb-2.5 mt-0.5 text-meta text-ink-500">{hint}</div>
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  );
}

const TRIGGER: Record<string, string> = {
  first_check: 'First check',
  schedule: 'Scheduled check',
  check_now: 'Checked now',
  read_now: 'Read now',
  retry: 'Read again',
};

function howText(group: KbProposedGroup): string {
  if (!group.run) return 'Added by a curator';
  return TRIGGER[group.run.trigger] ?? (group.run.kind === 'read' ? 'Read' : 'Checked');
}

/** The source and its quote check, as the prototype words them. */
function foundText(group: KbProposedGroup, locator: string | null | undefined): string {
  return group.source.kind === 'web_page'
    ? `Quote found on the captured page, ${formatDate(group.version.captured_at)}`
    : `Quote found in the document${locator ? `, ${locator}` : ''}`;
}

function Found({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-1.5 flex items-center gap-[5px] text-meta text-posture-healthy">
      <Check className="h-3 w-3" aria-hidden="true" /> {children}
    </div>
  );
}

type Mode = 'cite' | 'new';

function CaptureCard({
  group,
  products,
  onResult,
}: {
  group: KbProposedGroup;
  products: KbProduct[];
  onResult: (r: ReviewResult) => void;
}) {
  const { toast } = useToast();
  const confirm = useConfirmItems();
  const decline = useDeclineItems();
  const citationAction = useCitationAction();
  const patch = usePatchProposal();
  const [picked, setPicked] = React.useState<Set<string>>(
    () =>
      new Set([
        ...group.items.filter((i) => i.preselected).map((i) => i.id),
        ...group.citations.filter((c) => (c.confidence ?? 0) >= 0.85).map((c) => c.id),
      ]),
  );
  const [modes, setModes] = React.useState<Record<string, Mode>>({});
  const [drafts, setDrafts] = React.useState<Record<string, string>>({});
  const [errors, setErrors] = React.useState<Record<string, string | null>>({});
  const [declining, setDeclining] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const total = group.items.length + group.citations.length;
  const pickedItems = group.items.filter((i) => picked.has(i.id));
  const pickedCitations = group.citations.filter((c) => picked.has(c.id));
  const n = pickedItems.length + pickedCitations.length;
  const toggle = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const labelOf = (id: string) => {
    const item = group.items.find((i) => i.id === id);
    return (item?.statement ?? item?.excerpt_text ?? '').slice(0, 80);
  };

  const confirmPicked = async () => {
    setBusy(true);
    const result = emptyResult();
    try {
      // Statements typed but not yet saved are saved first.
      for (const item of pickedItems) {
        const draft = drafts[item.id];
        const clean = draft?.trim().replace(/\s+/g, ' ');
        if (clean !== undefined && clean !== (item.statement ?? '') && clean.length > 0) {
          try {
            await patch.mutateAsync({ id: item.id, body: { statement: clean } });
          } catch (err) {
            setErrors((e) => ({ ...e, [item.id]: errorMessage(err) }));
            toast({
              message: 'Nothing confirmed yet: a statement needs a change.',
              tone: 'critical',
            });
            return;
          }
        }
      }
      // A further citation chosen as a new item becomes a proposal of its own first.
      const splitIds: string[] = [];
      for (const c of pickedCitations.filter((c) => modes[c.id] === 'new')) {
        try {
          const out = (await citationAction.mutateAsync({ id: c.id, verb: 'split' })) as {
            item?: { id?: string };
          };
          if (out.item?.id) splitIds.push(out.item.id);
        } catch (err) {
          result.failed.push(errorMessage(err));
        }
      }
      const ids = [...pickedItems.map((i) => i.id), ...splitIds];
      if (ids.length > 0) {
        const reply = await confirm.mutateAsync({ ids });
        result.confirmed.push(...reply.confirmed);
        result.becameCitation.push(
          ...reply.became_citation.map((b) => ({ id: b.id, itemNumber: b.item_number })),
        );
        result.skipped.push(...reply.skipped.map((s) => ({ ...s, label: labelOf(s.id) })));
      }
      for (const c of pickedCitations.filter((c) => (modes[c.id] ?? 'cite') === 'cite')) {
        try {
          await citationAction.mutateAsync({ id: c.id, verb: 'accept' });
          result.accepted.push({ itemNumber: c.item.number });
        } catch (err) {
          result.failed.push(errorMessage(err));
        }
      }
      onResult(result);
    } catch (err) {
      toast({ message: errorMessage(err), tone: 'critical' });
    } finally {
      setBusy(false);
    }
  };

  const declinePicked = async (code: string, text?: string) => {
    setBusy(true);
    try {
      const itemIds = pickedItems.map((i) => i.id);
      if (itemIds.length > 0) {
        await decline.mutateAsync({
          ids: itemIds,
          reason_code: code,
          ...(text ? { reason_text: text } : {}),
        });
      }
      for (const c of pickedCitations) {
        await citationAction.mutateAsync({
          id: c.id,
          verb: 'decline',
          body: { reason_code: code, ...(text ? { reason_text: text } : {}) },
        });
      }
      toast({
        message: `Declined ${plural(itemIds.length + pickedCitations.length, 'passage')}. They stay on record.`,
      });
      setDeclining(false);
    } catch (err) {
      toast({ message: errorMessage(err), tone: 'critical' });
    } finally {
      setBusy(false);
    }
  };

  const web = group.source.kind === 'web_page';
  const title = group.source.title ?? group.source.file_name ?? 'Untitled source';
  return (
    <div
      role="group"
      aria-label={`Proposals from ${title}`}
      className="overflow-hidden rounded-card border border-ai-surface-border bg-paper-0"
    >
      <div className="flex items-center gap-2.5 border-b border-ai-surface-border bg-paper-0 px-4 py-3">
        {web ? (
          <Globe className="h-4 w-4 flex-none text-ink-500" aria-hidden="true" />
        ) : (
          <FileText className="h-4 w-4 flex-none text-ink-500" aria-hidden="true" />
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate text-body font-semibold text-ink-900">{title}</div>
          <div className="truncate text-meta text-ink-500">
            {web && group.source.url ? `${hostPath(group.source.url)} · ` : ''}
            {web ? 'captured' : 'received'} {formatDate(group.version.captured_at)} ·{' '}
            {howText(group)}
          </div>
        </div>
        <span className="flex-none whitespace-nowrap text-meta text-ink-500">
          {plural(total, 'passage')}
        </span>
      </div>
      <div className="flex flex-col gap-2.5 bg-ai-surface p-3">
        {group.items.map((p) => (
          <ProposalCard
            key={p.id}
            group={group}
            proposal={p}
            products={products}
            picked={picked.has(p.id)}
            onToggle={() => toggle(p.id)}
            draft={drafts[p.id] ?? p.statement ?? ''}
            setDraft={(v) => setDrafts((d) => ({ ...d, [p.id]: v }))}
            error={errors[p.id] ?? null}
            setError={(e) => setErrors((x) => ({ ...x, [p.id]: e }))}
          />
        ))}
        {group.citations.map((c) => (
          <OfferedCard
            key={c.id}
            group={group}
            citation={c}
            picked={picked.has(c.id)}
            onToggle={() => toggle(c.id)}
            mode={modes[c.id] ?? 'cite'}
            setMode={(m) => setModes((x) => ({ ...x, [c.id]: m }))}
          />
        ))}
      </div>
      <div className="border-t border-ai-surface-border bg-paper-0 px-4 py-2.5">
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex-1 text-meta text-ink-500">
            {n} of {total} selected
          </span>
          <WorkspaceButton
            variant="tertiary"
            disabled={n === 0 || busy}
            onClick={() => setDeclining((d) => !d)}
          >
            Decline selected
          </WorkspaceButton>
          <WorkspaceButton
            variant="primary"
            disabled={n === 0 || busy}
            onClick={() => void confirmPicked()}
          >
            Confirm {n} selected
          </WorkspaceButton>
        </div>
        {declining && n > 0 ? (
          <DeclineChooser
            label={`Why decline ${plural(n, 'passage')}?`}
            pending={busy}
            onDecline={(code, text) => void declinePicked(code, text)}
            onCancel={() => setDeclining(false)}
          />
        ) : null}
      </div>
    </div>
  );
}

function ProposalCard({
  group,
  proposal: p,
  products,
  picked,
  onToggle,
  draft,
  setDraft,
  error,
  setError,
}: {
  group: KbProposedGroup;
  proposal: KbProposal;
  products: KbProduct[];
  picked: boolean;
  onToggle: () => void;
  draft: string;
  setDraft: (v: string) => void;
  error: string | null;
  setError: (e: string | null) => void;
}) {
  const { toast } = useToast();
  const decline = useDeclineItems();
  const [open, setOpen] = React.useState<'change' | 'narrow' | 'decline' | null>(null);
  const edited = p.ai_statement !== null && p.statement !== p.ai_statement;
  const where = [
    p.product?.name ?? (p.product_id ? 'Product' : 'All products'),
    p.module?.name ?? p.module?.label ?? p.module_label,
    p.process_area,
  ].filter(Boolean);
  return (
    <div
      className={cn(
        'rounded-card border border-l-4 border-ink-100 bg-paper-0 px-3.5 py-3',
        picked ? 'border-l-accent-amber-600' : 'border-l-ai-surface-border',
      )}
    >
      <div className="flex items-start gap-3">
        <TriCheckbox
          state={picked ? 'on' : 'off'}
          onChange={onToggle}
          aria-label="Select this passage"
          className="mt-1"
        />
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2">
            {p.origin === 'curator' ? (
              <Chip>Added by a curator</Chip>
            ) : (
              <Chip tone="ai">
                <Zap className="h-[11px] w-[11px]" aria-hidden="true" /> Proposed by AI
              </Chip>
            )}
            {edited ? <span className="text-meta text-ink-500">Statement edited</span> : null}
            <span className="flex-1" />
            {p.confidence !== null ? <AiConfidenceChip value={p.confidence} /> : null}
          </div>
          <StatementInput
            proposal={p}
            value={draft}
            onChange={setDraft}
            onSaved={() => undefined}
            error={error}
            setError={setError}
          />
          <Quote text={p.excerpt_text} />
          <Found>
            {foundText(group, p.locator_label)}
            {open !== 'narrow' ? (
              <>
                <span className="text-ink-300" aria-hidden="true">
                  ·
                </span>
                <Linkish tone="quiet" onClick={() => setOpen('narrow')}>
                  Narrow the quote
                </Linkish>
              </>
            ) : null}
          </Found>
          {p.kind === 'requirement_response' &&
          !p.library_item_id &&
          p.library_suggestions?.length ? (
            <LibraryChoice proposal={p} />
          ) : null}
        </div>
      </div>
      <div className="mt-2.5 flex items-center justify-between gap-2 pl-7">
        <span className="flex min-w-0 items-center gap-2">
          <KindChip kind={p.kind} />
          <span className="min-w-0 truncate text-meta text-ink-500" title={where.join(' · ')}>
            {where.join(' · ')}
          </span>
          {open !== 'change' ? (
            <Linkish className="flex-none" onClick={() => setOpen('change')}>
              Change
            </Linkish>
          ) : null}
        </span>
        <Linkish tone="danger" onClick={() => setOpen(open === 'decline' ? null : 'decline')}>
          Decline
        </Linkish>
      </div>
      {open === 'change' ? (
        <ClassifyPickers proposal={p} products={products} onDone={() => setOpen(null)} />
      ) : null}
      {open === 'narrow' ? (
        <div className="ml-7">
          <NarrowQuote
            proposal={p}
            onDone={() => {
              setOpen(null);
              toast({ message: 'Quote narrowed. It was checked against the capture again.' });
            }}
            onCancel={() => setOpen(null)}
          />
        </div>
      ) : null}
      {open === 'decline' ? (
        <div className="ml-7">
          <DeclineChooser
            pending={decline.isPending}
            onCancel={() => setOpen(null)}
            onDecline={(code, text) =>
              decline.mutate(
                { ids: [p.id], reason_code: code, ...(text ? { reason_text: text } : {}) },
                {
                  onSuccess: () =>
                    toast({
                      message:
                        'Declined. It stays on record and isn’t proposed again from this capture.',
                    }),
                  onError: (err) => toast({ message: errorMessage(err), tone: 'critical' }),
                },
              )
            }
          />
        </div>
      ) : null}
    </div>
  );
}

/** The library requirements a requirement answer may be for (the server's top three). */
export function LibraryChoice({ proposal }: { proposal: KbProposal }) {
  const patch = usePatchProposal();
  const [error, setError] = React.useState<string | null>(null);
  return (
    <div className="mt-2.5 rounded-card border border-accent-amber-100 bg-accent-amber-50 px-3 py-2.5">
      <div className="text-meta font-medium text-ink-900">Which requirement does it answer?</div>
      <div className="mt-1.5 flex flex-col gap-1">
        {(proposal.library_suggestions ?? []).map((s) => (
          <button
            key={s.item_id}
            type="button"
            disabled={patch.isPending}
            onClick={() =>
              patch.mutate(
                { id: proposal.id, body: { library_item_id: s.item_id } },
                { onError: (err) => setError(errorMessage(err)) },
              )
            }
            className="flex items-baseline gap-2 rounded-md px-1.5 py-1 text-left text-body-sm text-ink-700 hover:bg-paper-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-amber-600"
          >
            <MonoNo>{s.number}</MonoNo>
            <span>{s.text}</span>
          </button>
        ))}
      </div>
      {error ? <div className="mt-1 text-meta text-posture-critical">{error}</div> : null}
    </div>
  );
}

function OfferedCard({
  group,
  citation: c,
  picked,
  onToggle,
  mode,
  setMode,
}: {
  group: KbProposedGroup;
  citation: KbOfferedCitation;
  picked: boolean;
  onToggle: () => void;
  mode: Mode;
  setMode: (m: Mode) => void;
}) {
  const { toast } = useToast();
  const action = useCitationAction();
  const [declining, setDeclining] = React.useState(false);
  const number = c.item.number ?? 'an item';
  return (
    <div
      className={cn(
        'rounded-card border border-l-4 border-ink-100 bg-paper-0 px-3.5 py-3',
        picked ? 'border-l-accent-amber-600' : 'border-l-ai-surface-border',
      )}
    >
      <div className="flex items-start gap-3">
        <TriCheckbox
          state={picked ? 'on' : 'off'}
          onChange={onToggle}
          aria-label="Select this passage"
          className="mt-1"
        />
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2">
            <Chip tone="ai">
              <Zap className="h-[11px] w-[11px]" aria-hidden="true" /> Proposed by AI
            </Chip>
            <span className="flex-1" />
            {c.confidence !== null ? <AiConfidenceChip value={c.confidence} /> : null}
          </div>
          <Quote text={c.excerpt_text} />
          <Found>{foundText(group, c.locator_label)}</Found>
          <div
            className={cn(
              'mt-2.5 flex flex-wrap items-center gap-2.5 rounded-card border px-3.5 py-3',
              mode === 'cite'
                ? 'border-posture-healthy bg-posture-healthy-tint'
                : 'border-accent-amber-100 bg-accent-amber-50',
            )}
          >
            <Link2 className="h-4 w-4 flex-none text-accent-amber-700" aria-hidden="true" />
            <div className="min-w-[180px] flex-1">
              <div className="font-medium text-ink-900">Means the same as {number}</div>
              <div className="truncate text-meta text-ink-500">{c.item.statement}</div>
            </div>
            <SegmentedControl
              aria-label="How to add it"
              value={mode}
              onChange={setMode}
              className="flex-none bg-paper-0 [&>button]:h-[26px] [&>button]:px-2"
              options={[
                { value: 'cite', label: 'Add as a further citation' },
                { value: 'new', label: 'Make it a new item' },
              ]}
            />
          </div>
        </div>
      </div>
      <div className="mt-2.5 flex items-center justify-between gap-2 pl-7">
        <span className="text-meta text-ink-500">
          {mode === 'cite'
            ? `Adds this quote to ${number}, which stays as it is`
            : 'Becomes an item of its own, numbered when you confirm it'}
        </span>
        <Linkish tone="danger" onClick={() => setDeclining((d) => !d)}>
          Decline
        </Linkish>
      </div>
      {declining ? (
        <div className="ml-7">
          <DeclineChooser
            pending={action.isPending}
            onCancel={() => setDeclining(false)}
            onDecline={(code, text) =>
              action.mutate(
                {
                  id: c.id,
                  verb: 'decline',
                  body: { reason_code: code, ...(text ? { reason_text: text } : {}) },
                },
                {
                  onSuccess: () => toast({ message: 'Declined. It stays on record.' }),
                  onError: (err) => toast({ message: errorMessage(err), tone: 'critical' }),
                },
              )
            }
          />
        </div>
      ) : null}
    </div>
  );
}
